import { useEffect, useRef, useState, forwardRef, createContext, useContext } from 'react';
import {
    ChevronRight,
    ChevronDown,
    FileText,
    Folder,
    FolderOpen,
    Plus,
    MoreHorizontal,
    Settings,
    Archive,
    Loader2,
    GripVertical,
} from 'lucide-react';
import { sortableId } from './useDocTreeDnd';
import { formatArchiveDate } from './docTreeApi';
import { Picto } from '../../shared/ui/PictoIcon';

/* ── Inline text input ── */
export function InlineInput({ defaultValue, placeholder, onConfirm, onCancel, autoFocus = true }) {
    const ref = useRef(null);
    const cancelledRef = useRef(false);
    const [value, setValue] = useState(defaultValue || '');

    useEffect(() => {
        if (autoFocus && ref.current) ref.current.focus();
    }, [autoFocus]);

    const submit = () => {
        if (cancelledRef.current) return;
        const v = value.trim();
        if (v) onConfirm(v);
        else onCancel();
    };

    return (
        <input
            ref={ref}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
                if (e.key === 'Enter') submit();
                if (e.key === 'Escape') { cancelledRef.current = true; onCancel(); }
            }}
            onBlur={submit}
            placeholder={placeholder}
            className="w-full text-xs px-2 py-1 bg-white border border-blue-400 rounded-md outline-none ring-2 ring-blue-200/50 text-stone-800 placeholder:text-stone-300"
        />
    );
}

/* ── Tree row ── */
export function TreeItemRow({ item, depth, expandedDirs, loadingDirs, onToggle, activePath, onContextMenu, onNavigate, onSettings, renaming, onRenameConfirm, onRenameCancel, isAdmin, dragHandleProps, isDragging }) {
    const isDir = item.type === 'directory';
    const isExpanded = expandedDirs.has(item.id);
    const isLoading = loadingDirs.has(item.id);
    const isArchived = !!item.archived;
    const href = isDir ? `/docs/space/${item.id}` : `/docs/page/${item.id}`;
    const isActive = activePath === href;

    const handleClick = (e) => { e.preventDefault(); isDir ? onToggle(item.id) : onNavigate(href); };
    const handleCtx = (e) => { e.preventDefault(); e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); onContextMenu(item, r.right, r.bottom + 4, 'below-right'); };
    const handleSettings = (e) => { e.preventDefault(); e.stopPropagation(); onSettings?.(item); };
    const handleNativeCtx = (e) => { e.preventDefault(); onContextMenu(item, e.clientX, e.clientY); };

    if (renaming) {
        return (
            <div className="py-0.5" style={{ paddingLeft: `${depth * 20 + 10}px`, paddingRight: 8 }}>
                <InlineInput defaultValue={isDir ? item.name : item.title} placeholder="Nom…" onConfirm={onRenameConfirm} onCancel={onRenameCancel} />
            </div>
        );
    }

    return (
        <a href={href} onClick={handleClick} onContextMenu={handleNativeCtx}
            className={`flex items-center gap-1.5 py-1.5 px-2 rounded-lg text-xs cursor-pointer transition-all duration-150 group w-full relative select-none ${
                isDragging ? 'opacity-40 bg-blue-50 ring-1 ring-blue-300' :
                isArchived
                    ? 'text-stone-400 bg-amber-50/40 border-l-2 border-amber-300 hover:bg-amber-50'
                    : isActive
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900'
            }`}
            style={{ paddingLeft: `${depth * 20 + 10}px` }}
            title={isArchived ? `Archivé le ${formatArchiveDate(item.archived)}` : undefined}
        >
            {isActive && !isArchived && !isDragging && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 bg-blue-500 rounded-r-full" />}
            {dragHandleProps && (
                <span {...dragHandleProps} className="flex-shrink-0 w-3 text-stone-300 hover:text-stone-500 cursor-grab active:cursor-grabbing touch-none opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Réorganiser">
                    <GripVertical size={12} />
                </span>
            )}
            {isDir ? (
                <>
                    <span className={`flex-shrink-0 w-4 transition-colors ${isArchived ? 'text-amber-400' : isActive ? 'text-blue-500' : 'text-stone-400 group-hover:text-stone-500'}`}>
                        {isLoading ? <Loader2 size={14} className="animate-spin" /> : isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </span>
                    <span className={`flex-shrink-0 w-4 transition-colors ${isArchived ? 'text-amber-400' : isActive ? 'text-blue-500' : 'text-stone-400 group-hover:text-stone-500'}`}>
                        {isArchived ? <Archive size={14} /> : isExpanded ? <FolderOpen size={14} /> : <Folder size={14} />}
                    </span>
                </>
            ) : (
                <>
                    <span className="w-4 flex-shrink-0" />
                    <span className={`flex-shrink-0 w-4 transition-colors ${isArchived ? 'text-amber-400' : isActive ? 'text-blue-500' : 'text-stone-400 group-hover:text-stone-500'}`}>
                        {isArchived ? <Archive size={14} /> : <FileText size={14} />}
                    </span>
                </>
            )}
            <span className="truncate flex-1">{!isDir && item.icon ? <Picto name={item.icon} size={13} className="inline-block align-[-2px] mr-1" /> : null}{isDir ? item.name : item.title}</span>
            {!(isArchived && !isAdmin) && (
                <span className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 flex items-center gap-0.5">
                    {!isArchived && (
                        <span onClick={handleSettings} className="p-0.5 hover:bg-stone-200 rounded text-stone-400 hover:text-stone-600 cursor-pointer" aria-label="Paramètres">
                            <Settings size={12} />
                        </span>
                    )}
                    <span onClick={handleCtx} className="p-0.5 hover:bg-stone-200 rounded text-stone-400 hover:text-stone-600 cursor-pointer" aria-label="Actions">
                        {isArchived ? <MoreHorizontal size={12} /> : isDir ? <Plus size={12} /> : <MoreHorizontal size={12} />}
                    </span>
                </span>
            )}
        </a>
    );
}

/* ── Context for DnD tree items ── */
export const DndTreeCtx = createContext(null);

export const DndTreeItemComponent = forwardRef(function DndTreeItem(props, ref) {
    const ctx = useContext(DndTreeCtx);
    const { item, depth, handleProps, ghost, clone, wrapperRef, style } = props;
    const srcItem = item._item;
    if (!srcItem) return null;

    if (clone) {
        return (
            <div ref={ref} className="doc-tree-drag-overlay">
                <div className="flex items-center gap-1.5 py-1.5 px-3 rounded-lg text-xs bg-white shadow-lg ring-1 ring-blue-200 text-stone-700 font-medium max-w-[220px]">
                    {srcItem.type === 'directory'
                        ? <Folder size={14} className="text-blue-500 flex-shrink-0" />
                        : <FileText size={14} className="text-blue-500 flex-shrink-0" />}
                    <span className="truncate">{srcItem.type !== 'directory' && srcItem.icon ? <Picto name={srcItem.icon} size={13} className="inline-block align-[-2px] mr-1" /> : null}{srcItem.type === 'directory' ? srcItem.name : srcItem.title}</span>
                </div>
            </div>
        );
    }

    const key = sortableId(srcItem);

    return (
        <li ref={wrapperRef} style={{ ...style, listStyle: 'none' }}>
            <div ref={ref}>
                <TreeItemRow
                    item={srcItem}
                    depth={depth}
                    expandedDirs={ctx.expandedDirs}
                    loadingDirs={ctx.loadingDirs}
                    onToggle={ctx.handleToggle}
                    activePath={ctx.activePath}
                    onContextMenu={ctx.onContextMenu}
                    onNavigate={ctx.navigate}
                    onSettings={ctx.onSettings}
                    renaming={ctx.renamingId === key}
                    onRenameConfirm={(newName) => ctx.handleRenameConfirm(srcItem, newName)}
                    onRenameCancel={ctx.onRenameCancel}
                    isAdmin={ctx.isAdmin}
                    dragHandleProps={ctx.canDrag && !srcItem.archived ? handleProps : null}
                    isDragging={ghost}
                />
            </div>
        </li>
    );
});
